import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Stepper } from "@shared/components/Stepper";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { SharedAccountSelection } from "../shared/sharedAccountSelection";
import { ReminderDetails } from "./stepComponents/reminderDetails/reminderDetails";
import { SharedReviewSend } from "../shared/sharedReviewSend";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Users,
  Download,
  AlertCircle,
} from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { PageHeader } from "@shared/components/PageHeader";

interface Consumer {
  consumerId: number;
  consumerNo: string;
  fullName: string;
  status: string;
  totalOutstanding: number;
  agingBucket: string;
  riskLevel: number;
}

export const BulkRemindersCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const remoteUtilityId = useRemoteUtilityId();
  const storageKey = `bulk-reminders-wizard-progress-${remoteUtilityId}`;

  // Result dashboard state
  const [isDispatched, setIsDispatched] = useState(false);
  const [dispatchResults, setDispatchResults] = useState<{
    recipients: Consumer[];
    template: string;
    channel: string;
    approvedBy: string;
    refNo: string;
    totalCount: number;
    dispatchedCount: number;
    failedCount: number;
    failedItems: Array<{ consumer: Consumer; error: string }>;
  } | null>(null);

  // Cleanup local storage when navigating away from the wizard
  useEffect(() => {
    return () => {
      if (window.location.pathname !== "/cx/outstanding/create/bulk-reminder") {
        localStorage.removeItem(storageKey);
      }
    };
  }, [location.pathname, storageKey]);

  const handleComplete = useCallback((stepData: any) => {
    const step1 = stepData[0] || {};
    const step2 = stepData[1] || {};
    const step3 = stepData[2] || {};

    console.log(step1, step2, step3);

    const consumers: Consumer[] = step1.selectedConsumers || [];
    const template = step2.reminderTemplate || "First Reminder (14‑day window)";
    const channel = step2.communicationChannel || "Email";
    const approvedBy = step3.approvedBy || "Sarah Jenkins (CSO Manager)";
    const refNo =
      step3.refNo || `RMD-${Math.floor(100000 + Math.random() * 900000)}`;

    const totalCount = consumers.length;
    const failedCount = totalCount > 0 ? 1 : 0; // Simulate a single failure for demo
    const dispatchedCount = Math.max(0, totalCount - failedCount);

    const failedItems: Array<{ consumer: Consumer; error: string }> = [];
    if (totalCount > 0) {
      failedItems.push({
        consumer: consumers[0],
        error:
          channel === "Email"
            ? "Invalid Email Address"
            : channel === "SMS"
              ? "Missing Mobile Number"
              : "Address Incomplete",
      });
    }

    setDispatchResults({
      recipients: consumers,
      template,
      channel,
      approvedBy,
      refNo,
      totalCount,
      dispatchedCount,
      failedCount,
      failedItems,
    });
    setIsDispatched(true);
  }, []);

  const handleDone = useCallback(() => {
    localStorage.removeItem(storageKey);
    navigate("/cx/outstanding");
  }, [storageKey, navigate]);

  const handleDownloadFullReport = () => {
    if (!dispatchResults) return;
    let csv = "data:text/csv;charset=utf-8,";
    csv +=
      "Batch Ref,Template,Channel,Approved By,Account No,Consumer,Outstanding,Status\n";
    dispatchResults.recipients.forEach((c) => {
      const isFailed = dispatchResults.failedItems.some(
        (f) => String(f.consumer.consumerId) === String(c.consumerId),
      );
      const status = isFailed ? "Failed" : "Dispatched";
      csv += `"${dispatchResults.refNo}","${dispatchResults.template}","${dispatchResults.channel}","${dispatchResults.approvedBy}","${c.consumerNo}","${c.fullName}",$${c.totalOutstanding.toFixed(2)},"${status}"\n`;
    });
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute(
      "download",
      `Reminder_Batch_Full_Report_${dispatchResults.refNo}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadErrorReport = () => {
    if (!dispatchResults) return;
    let csv = "data:text/csv;charset=utf-8,";
    csv += "Account No,Consumer,Outstanding,Error\n";
    dispatchResults.failedItems.forEach((f) => {
      csv += `"${f.consumer.consumerNo}","${f.consumer.fullName}",$${f.consumer.totalOutstanding.toFixed(2)},"${f.error}"\n`;
    });
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute(
      "download",
      `Reminder_Batch_Error_Report_${dispatchResults.refNo}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: TableColumn<Consumer>[] = useMemo(
    () => [
      {
        header: "Consumer Name",
        key: "fullName",
        render: (v) => (
          <span className="font-semibold text-neutral-800">{v}</span>
        ),
      },
      {
        header: "Account No.",
        key: "consumerNo",
        render: (v) => <span className="text-neutral-400">{v}</span>,
      },
      {
        header: "Outstanding",
        key: "totalOutstanding",
        render: (v) => (
          <span className="font-bold text-neutral-700">
            ${Number(v).toFixed(2)}
          </span>
        ),
      },
      {
        header: "Delivery Status",
        key: "consumerId",
        render: (id, row) => {
          const failed = dispatchResults?.failedItems.find(
            (f) => String(f.consumer.consumerId) === String(id),
          );
          if (failed) {
            return (
              <div className="flex items-center gap-1.5 text-red-600 font-semibold">
                <XCircle size={14} className="text-red-500" />
                <span>Failed: {failed.error}</span>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1.5 text-emerald-600 font-semibold">
              <CheckCircle2 size={14} className="text-emerald-500" />
              <span>Dispatched via {dispatchResults?.channel}</span>
            </div>
          );
        },
      },
    ],
    [dispatchResults],
  );

  const steps = useMemo(
    () => [
      {
        title: "Select Accounts",
        slug: "select-accounts",
        component: (
          <SharedAccountSelection type="reminder" storageKey={storageKey} />
        ),
        optional: false,
      },
      {
        title: "Channel & Template",
        slug: "channel-template",
        component: <ReminderDetails />,
        optional: false,
      },
      {
        title: "Review & Send",
        slug: "review-send",
        component: <SharedReviewSend type="reminder" />,
        optional: false,
      },
    ],
    [storageKey],
  );

  if (!remoteUtilityId) {
    return (
      <div className="flex items-center justify-center p-12 bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="animate-pulse text-gray-500 font-semibold text-sm">
          Loading configuration…
        </div>
      </div>
    );
  }

  if (isDispatched && dispatchResults) {
    // Result dashboard UI
    return (
      <div className="space-y-6 animate-in fade-in zoom-in-95 duration-500">
        {/* Header */}
        <div className="flex justify-between items-center bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              <h1 className="text-xl font-bold text-neutral-800">
                Bulk Reminders Dispatch Completed
              </h1>
            </div>
            <p className="text-xs text-neutral-400 mt-1.5 pl-8">
              Reminder logs successfully committed. Review batch statistics
              below.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadFullReport}
              className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 text-xs font-semibold rounded-lg h-9"
            >
              <Download size={14} /> Download Report
            </Button>
            {dispatchResults.failedCount > 0 && (
              <Button
                onClick={handleDownloadErrorReport}
                className="bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 flex items-center gap-1.5 text-xs font-semibold rounded-lg h-9"
              >
                <AlertCircle size={14} /> Download Error Report
              </Button>
            )}
            <Button
              onClick={() => setIsDispatched(false)}
              className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 text-xs font-semibold rounded-lg h-9 px-4"
            >
              <ArrowLeft size={14} /> Modify Request
            </Button>
            <Button
              onClick={handleDone}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5 text-xs font-semibold rounded-lg h-9 px-4"
            >
              Done
            </Button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <QuickStatsCard
            title="Total Recipients"
            count={dispatchResults.totalCount}
            icon={<Users className="h-8 w-8 text-blue-500" />}
          />
          <QuickStatsCard
            title="Dispatched"
            count={dispatchResults.dispatchedCount}
            icon={<CheckCircle2 className="h-8 w-8 text-emerald-500" />}
          />
          <QuickStatsCard
            title="Failed"
            count={dispatchResults.failedCount}
            icon={<XCircle className="h-8 w-8 text-rose-500" />}
          />
        </div>

        {/* Metadata summary */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">
            Batch Metadata Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">
                Batch Ref.
              </span>
              <span className="font-semibold text-neutral-800">
                {dispatchResults.refNo}
              </span>
            </div>
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">Channel</span>
              <span className="font-semibold text-neutral-800">
                {dispatchResults.channel}
              </span>
            </div>
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">
                Approved By
              </span>
              <span className="font-semibold text-neutral-800">
                {dispatchResults.approvedBy}
              </span>
            </div>
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">Template</span>
              <span className="font-semibold text-neutral-800">
                {dispatchResults.template}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery logs table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden p-5 space-y-4">
          <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">
            Reminder Delivery Logs
          </h4>
          <div className="border border-gray-150 rounded-lg overflow-hidden">
            <GenericTable<Consumer>
              data={dispatchResults.recipients}
              columns={columns}
              rowKey="id"
              selectable={false}
            />
          </div>
        </div>
      </div>
    );
  }

  // Wizard UI
  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              localStorage.removeItem(storageKey);
              navigate("/cx/outstanding");
            }}
          >
            <ArrowLeft size={16} />
          </Button>
          <PageHeader
            title="New Bulk Reminder Request"
            subtitle="Outstanding account balance batch reminders setup"
          />
        </div>
      </div>
      <div className="bg-white p-6 border border-gray-200 rounded-xl shadow-sm">
        <Stepper
          steps={steps}
          onStepChange={() => {}}
          onValidation={() => true}
          onStepSave={async () => {}}
          enableApiSave={false}
          saveOnStepChange={false}
          trackDirtyState={false}
          autoSave={true}
          persistProgress={true}
          allowSkip={false}
          storageKey={storageKey}
          enableRouting={true}
          onComplete={handleComplete}
          showDraftResume={false}
        />
      </div>
    </div>
  );
};

export default BulkRemindersCreate;
